import React, { useEffect, useState, useRef } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  DatePicker,
  Table,
  Tag,
  Avatar,
  Modal,
  Upload,
  Button,
  Popconfirm,
  Spin,
  Popover
} from 'antd';
import { faClipboardCheck, faEye, faFileExcel, faPencil, faPlus, faTrash } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'antd/es/typography/Link';
import { userAtom, notificationsAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import { H1, H4 } from '../../../shared-components/typography/Title';
import api from '../../../../api/api';
import {
  toCurrency,
  thousand,
  getDatePresets,
  getReceivingReportColumns,
  cleanReceivingReportDataForExport,
  getReceivingReportExportFields
} from '../../../../functions';
import SelectMultiple from '../../../shared-components/form/SelectMultiple';
import CustomButton from '../../../shared-components/buttons/Button';
import Form from '../../../shared-components/form/Form';
import DateSelector from '../../../shared-components/form/DatePicker';
import Input from '../../../shared-components/form/Input';
import envConfig from '../../../../envConfig';

function Receiving() {
  const { t, i18n } = useTranslation();
  const chartRef = useRef();
  const { RangePicker } = DatePicker;
  const user = useRecoilValue(userAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedPartNumbers, setSelectedPartNumbers] = useState([]);
  const [selectedPos, setSelectedPos] = useState([]);
  const [selectedProcessAreas, setSelectedProcessAreas] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null);

  const [loadingModal, setLoadingModal] = useState(false);
  const [modalReportData, setModalReportData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  function getData(sTerm) {
    if (!dateRange.start || !dateRange.end) {
      return;
    }
    setLoading(true);
    setLastSearch(sTerm);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving/graph', {
        StartDate: startString,
        EndDate: endString,
        Query: sTerm,
        Categories: selectedCategories,
        ProcessAreas: selectedProcessAreas,
        Manufacturers: selectedManufacturers,
        PartNumbers: selectedPartNumbers,
        PoNumbers: selectedPos
      })
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function exportData() {
    setLoadingExport(true);
    const startDate = dayjs(dateRange.start);
    const endDate = dayjs(dateRange.end);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.endOf('day').add(-1, 'second').add(-1 * utcOffset, 'minute').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving/graph/excel', {
        StartDate: startString,
        EndDate: endString,
        Query: lastSearch,
        Categories: selectedCategories,
        ProcessAreas: selectedProcessAreas,
        Manufacturers: selectedManufacturers,
        PartNumbers: selectedPartNumbers,
        PoNumbers: selectedPos
      }, {
        responseType: 'blob'
      })
      .then((response) => {
        setLoadingExport(false);
        const fileName = `${t('charts')} - ${t('receiving')} ${startDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}-${endDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}.xlsx`;
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        setLoadingExport(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function exportSetData() {
    if (!selectedRange) {
      return;
    }
    const startDate = dayjs(selectedRange.startDate);
    const endDate = dayjs(selectedRange.endDate);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1, 'second').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving/excel', {
      StartDate: startString,
      EndDate: endString,
      Query: lastSearch,
      Categories: selectedCategories,
      ProcessAreas: selectedProcessAreas,
      Manufacturers: selectedManufacturers,
      PartNumbers: selectedPartNumbers,
      PoNumbers: selectedPos,
      ForExport: true,
    }, {
      responseType: 'blob'
    })
      .then((response) => {
        setLoadingModal(false);
        const fileName = `${t('receiving_report')} ${startDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}-${endDate.add(-1 * utcOffset, 'minute').format('MM DD, YYYYY')}.xlsx`;
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => {
        setLoadingModal(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function isRangeMoreThanMonth(range) {
    try {
      const startDate = dayjs(range?.start);
      const endDate = dayjs(range?.end);
      const monthDiff = endDate.diff(startDate, 'month', true);
      return monthDiff > 1;
    } catch (err) {
      return false;
    }
  }

  function getModalData(selectedPage, sTerm) {
    if (!selectedRange) {
      return;
    }
    setLoadingModal(true);
    setCurrentPage(selectedPage);
    const startDate = dayjs(selectedRange.startDate);
    const endDate = dayjs(selectedRange.endDate);
    const utcOffset = startDate.utcOffset();
    const startString = startDate.format('YYYY-MM-DDThh:mm:ss');
    const endString = endDate.add(-1, 'second').format('YYYY-MM-DDThh:mm:ss');
    api.post('reports/receiving/graph/set', {
      StartDate: startString,
      EndDate: endString,
      Query: sTerm,
      Categories: selectedCategories,
      ProcessAreas: selectedProcessAreas,
      Manufacturers: selectedManufacturers,
      PartNumbers: selectedPartNumbers,
      PoNumbers: selectedPos,
      Page: selectedPage,
      Size: pageSize,
    })
      .then((response) => {
        setLoadingModal(false);
        if (response.data.success) {
          setModalReportData(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingModal(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    setModalReportData(null);
    if (selectedRange) {
      if (pageSize !== 20) {
        setPageSize(20);
      } else {
        getModalData(0, lastSearch);
      }
    }
  }, selectedRange);

  useEffect(() => {
    getModalData(0, lastSearch);
  }, [pageSize]); 

  useEffect(() => {
    setDateRange({
      start: dayjs().startOf('week').format('MMM DD, YYYY'),
      end: dayjs().endOf('day').subtract(1, 'second').format('MMM DD, YYYY')
    });
    setSelectedCategories([]);
    setSelectedProcessAreas([]);
    setSelectedManufacturers([]);
    setSelectedPartNumbers([]);
    setSelectedPos([]);
  }, []);

  useEffect(() => {
    getData(lastSearch);
  }, [dateRange]); // add filters sorts here

  return (
    <div className="space-y-2">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2">
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('process_areas')}
          id="processAreas"
          name="processAreas"
          value={selectedProcessAreas}
          onChange={(obj) => {
            setSelectedProcessAreas(obj);
          }}
          options={_.map(_.filter(reportData?.processAreas, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('categories')}
          id="categories"
          name="categories"
          value={selectedCategories}
          onChange={(obj) => {
            setSelectedCategories(obj);
          }}
          options={_.map(_.filter(reportData?.categories, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('manufacturers')}
          id="manufacturers"
          name="manufacturers"
          value={selectedManufacturers}
          onChange={(obj) => {
            setSelectedManufacturers(obj);
          }}
          options={_.map(_.filter(reportData?.manufacturers, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1"
          disabled={loading}
          label={t('part_number')}
          id="partnumbers"
          name="partnumbers"
          value={selectedPartNumbers}
          onChange={(obj) => {
            setSelectedPartNumbers(obj);
          }}
          options={_.map(_.filter(reportData?.partNumbers, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <SelectMultiple
          className="col-span-1 md:col-span-2 lg:col-span-2 lg:col-span-2 xl:col-span-1"
          disabled={loading}
          label={t('po_numbers')}
          id="ponumbers"
          name="ponumbers"
          value={selectedPos}
          onChange={(obj) => {
            setSelectedPos(obj);
          }}
          options={_.map(_.filter(reportData?.poNumbers, (c) => c?.length > 0), (c) => ({
            text: c,
            key: c,
            value: c
          }))}
        />
        <div className="col-span-1 md:col-span-2 lg:col-span-2 lg:col-span-2 xl:col-span-1">
          <RangePicker
            disabled={loading}
            className="w-full h-11 mt-2 bg-transparent xl:mt-6"
            disabledDate={(current) => current && current.valueOf() > Date.now()}
            presets={getDatePresets(t)}
            value={[
              dayjs(dateRange?.start),
              dayjs(dateRange?.end)
            ]}
            onChange={(obj) => {
              setDateRange({
                start: obj[0],
                end: obj[1]
              });
            }}
            format="MMM DD, YYYY"
          />
        </div>
      </div>
      <div className="flex space-x-2">
        <Input
          label={t('search')}
          type="text"
          name="search"
          key="search"
          value={searchTerm}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              getData(searchTerm)
            }
          }}
          onChange={(obj) => setSearchTerm(obj.target.value)}
        />
        <CustomButton
          className="bg-primaryColor px-4"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          loading={loading}
          text={t('search')}
          onClick={() => getData(searchTerm)}
        />
      </div>
      <div className="grid grid-cols-1">
        <div className="col-span-1">
          <Spin spinning={loading}>
            <div className="text-center" style={{ height: 450 }}>
              <Bar
                ref={chartRef}
                data={{
                  labels: _.map(reportData?.data, (s) => s.label),
                  datasets: [
                    {
                      label: t('received'),
                      borderColor: envConfig.BarGraphColor,
                      backgroundColor: `${envConfig.BarGraphColor}cc`,
                      data: _.map(reportData?.data, (x) => x.count)
                    }
                  ]
                }}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    yAxes: [{
                      ticks: {
                        beginAtZero: true,
                        callback: (value, index, ticks) => thousand(value)
                      }
                    }],
                  },
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'top',
                    },
                    title: {
                      display: false,
                    },
                    datalabels: {
                      anchor: 'end',
                      offset: 8,
                      rotation: 330,
                      color: 'black',
                      formatter: (value, context) => thousand(value),
                      labels: {
                        title: {
                          font: {
                            weight: 'regular'
                          }
                        }
                      }
                    }
                  },
                }}
              />
            </div>
          </Spin>
        </div>
        <Table
          className="col-span-1"
          bordered
          locale={{
            emptyText: t('no_data')
          }}
          size="small"
          title={() => (
            <div className="flex">
              <div className="flex items-center space-x-2 ml-auto">
                {isRangeMoreThanMonth(dateRange) && (
                  <Popover trigger="hover" content={t('max_of_one_month_for_export')}>
                    <div>
                      <CustomButton
                        className="bg-green-600 px-4"
                        textColor="text-primaryTextColor"
                        loaderColor="bg-primaryTextColor"
                        borderColor="bg-green-600"
                        loading={loading}
                        disabled
                        icon={faFileExcel}
                        text={t('export')}
                        onClick={() => exportData()}
                      />
                    </div>
                  </Popover>
                )}
                {!isRangeMoreThanMonth(dateRange) && (
                  <CustomButton
                    className="bg-green-600 px-4"
                    textColor="text-primaryTextColor"
                    loaderColor="bg-primaryTextColor"
                    borderColor="bg-green-600"
                    loading={loading}
                    icon={faFileExcel}
                    text={t('export')}
                    onClick={() => exportData()}
                  />
                )}
                <strong>{t('last_updated_on')}:</strong>
                {reportData?.lastUpdatedAt && (
                  <span>{dayjs(reportData?.lastUpdatedAt).add(-4, 'hour').format('MMM DD, YYYY hh:mm A')}</span>
                )}
              </div>
            </div>
          )}
          rowKey="id"
          loading={loading}
          pagination={false}
          dataSource={reportData?.data}
          columns={[
            {
              title: t('date'),
              key: 'label',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{row.label}</span>
              ),
            },
            {
              title: t('total'),
              key: 'total',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <span>{thousand(row.count)}</span>
              ),
            },
            {
              title: t('actions'),
              key: 'view',
              align: 'center',
              className: 'text-sm',
              render: (row, object, index) => (
                <CustomButton
                  className="bg-primaryColor px-4"
                  textColor="text-primaryTextColor"
                  loaderColor="bg-primaryTextColor"
                  borderColor="border-primaryBorderColor"
                  loading={row.loading}
                  text={t('view')}
                  onClick={() => setSelectedRange(row)}
                />
              ),
            },
          ]}
        />
      </div>
      {selectedRange && (
        <Modal
          title={`${t('received')} - ${selectedRange.label}`}
          open={selectedRange}
          centered
          width="100%"
          onCancel={() => setSelectedRange(null)}
          footer={[
            <Button
              key="close"
              type="primary"
              danger
              onClick={() => setSelectedRange(null)}
            >
              {t('close')}
            </Button>
          ]}
        >
          <Table
            bordered
            locale={{
              emptyText: t('no_data')
            }}
            title={() => (
              <div className="flex">
                <div className="space-x-2">
                  <strong>{t('total')}:</strong>
                  {modalReportData?.report && (
                    <span>{thousand(modalReportData?.report?.length)}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-auto">
                  {isRangeMoreThanMonth(selectedRange) && (
                    <Popover trigger="hover" content={t('max_of_one_month_for_export')}>
                      <div>
                        <CustomButton
                          className="bg-green-600 px-4"
                          textColor="text-primaryTextColor"
                          loaderColor="bg-primaryTextColor"
                          borderColor="bg-green-600"
                          loading={loading}
                          disabled
                          icon={faFileExcel}
                          text={t('export')}
                          onClick={() => exportSetData()}
                        />
                      </div>
                    </Popover>
                  )}
                  {!isRangeMoreThanMonth(selectedRange) && (
                    <CustomButton
                      className="bg-green-600 px-4"
                      textColor="text-primaryTextColor"
                      loaderColor="bg-primaryTextColor"
                      borderColor="bg-green-600"
                      loading={loading}
                      icon={faFileExcel}
                      text={t('export')}
                      onClick={() => exportSetData()}
                    />
                  )}
                  <strong>{t('last_updated_on')}:</strong>
                  {modalReportData?.lastUpdatedAt && (
                    <span>{dayjs(modalReportData?.lastUpdatedAt).format('MMM DD, YYYY hh:mm A')}</span>
                  )}
                </div>
              </div>
            )}
            rowKey="id"
            loading={loadingModal}
            className="mt-2"
            pagination={{
              pageSize,
              showSizeChanger: true,
              current: currentPage + 1,
              total: modalReportData?.total,
              onChange: (page, size) => {
                if (page - 1 !== currentPage) {
                  setCurrentPage(page - 1);
                  getModalData(page - 1);
                } else {
                  setPageSize(size);
                }
              }
            }}
            dataSource={modalReportData?.report}
            columns={getReceivingReportColumns(t)}
          />
        </Modal>
      )}
    </div>
  );
}

export default Receiving;
